<template>
  <TransitionRoot appear :show="isShown" as="template">
    <Dialog as="div" @close="() => {}" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 w-screen">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-lg transform rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <SpinnerLoader v-if="isSubmitting" />
              <div
                class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10"
              >
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('projects.form.title.' + type) }}
                </h3>
                <button
                  @click="$emit('close')"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>

              <div class="p-6 overflow-y-auto">
                <form @submit.prevent="submitForm" id="project-form" class="flex flex-col gap-5">
                  <fieldset :disabled="isSubmitting" class="contents :disabled:cursor-default">
                    <NewSubmission
                      v-if="type === FormType.VALIDATE && project"
                      :created-by="project.createdBy"
                      :created-at="project.createdAt"
                      :message="project.creatorMessage"
                      class="mb-4"
                    />

                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="projectName"
                        :label="$t('projects.form.fields.name.label')"
                        v-model="form.name.value.value"
                        :showErrorMessage="true"
                        :error="form.name.errorMessage.value"
                        @blur="form.name.handleChange"
                        :placeholder="$t('projects.form.fields.name.label')"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700 required">
                        {{ $t('projects.form.fields.description.label') }}
                      </label>
                      <TextEditor
                        v-model:content-model="form.description.value.value"
                        :parent-form-error="formError"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700">
                        {{ $t('actors.form.logo') }}
                      </label>
                      <ImagesLoader
                        @updateFiles="handleLogoUpdate"
                        :existingImages="existingLogo"
                        :uniqueImage="true"
                        :externalImagesLoader="false"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700">
                        {{ $t('projects.form.fields.deliverables.label') }}
                      </label>
                      <TextEditor
                        v-model:content-model="form.deliverables.value.value"
                        :parent-form-error="formError"
                        :min-length="0"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700">
                        {{ $t('projects.form.fields.calendar.label') }}
                      </label>
                      <TextEditor
                        v-model:content-model="form.calendar.value.value"
                        :parent-form-error="formError"
                        :min-length="0"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="website"
                        :label="$t('projects.form.fields.website.label')"
                        v-model="form.website.value.value"
                        :showErrorMessage="true"
                        :error="form.website.errorMessage.value"
                        @blur="form.website.handleChange"
                        :placeholder="$t('projects.form.fields.website.label')"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="status"
                        v-model="form.status.value.value"
                        :label="$t('projects.form.fields.status.label') + ' *'"
                        :options="Object.values(Status)"
                        :getLabel="(item) => $t('projects.status.' + item)"
                        :getValue="(item) => item"
                        :error="form.status.errorMessage.value"
                        @blur="form.status.handleChange(form.status.value.value)"
                      />
                    </div>

                    <div class="border-t border-gray-200 my-2"></div>

                    <FormSectionTitle :text="$t('projects.form.section.adminScope')" />

                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="administrativeScopes"
                        v-model="form.administrativeScopes.value.value"
                        :label="$t('actors.form.adminScope') + ' *'"
                        :options="Object.values(AdministrativeScope)"
                        :getLabel="(item) => $t('actors.scope.' + item)"
                        :getValue="(item) => item"
                        :error="form.administrativeScopes.errorMessage.value"
                        @blur="
                          form.administrativeScopes.handleChange(
                            form.administrativeScopes.value.value,
                          )
                        "
                        multiple
                      />
                    </div>

                    <Autocomplete
                      v-if="activeAdminLevels && activeAdminLevels.admin1"
                      v-model="form.admin1List.value.value"
                      :label="$t('actors.form.admin1')"
                      :items="adminBoundariesStore.admin1Boundaries"
                      :item-title="(item: any) => item.adm1Name"
                      :item-value="(item: any) => item"
                      multiple
                    />

                    <Autocomplete
                      v-if="activeAdminLevels && activeAdminLevels.admin3"
                      v-model="form.admin3List.value.value"
                      :label="$t('actors.form.admin3')"
                      :items="adminBoundariesStore.admin3Boundaries"
                      :item-title="(item: any) => item.adm3Name"
                      :item-value="(item: any) => item"
                      multiple
                    />

                    <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
                      <label class="block text-sm font-semibold text-gray-900 mb-3">
                        {{ $t('forms.banoc.title') }}
                      </label>
                      <div class="flex gap-4">
                        <div class="w-1/3 flex flex-col gap-1">
                          <BaseInput
                            id="banoc"
                            :label="$t('forms.banoc.code')"
                            v-model="form.banoc.value.value"
                            :showErrorMessage="true"
                            :error="form.banoc.errorMessage.value"
                            @blur="form.banoc.handleChange"
                            width="w-full"
                          />
                        </div>
                        <div class="w-2/3 flex flex-col gap-1">
                          <BaseInput
                            id="banocUrl"
                            :label="$t('forms.banoc.url')"
                            v-model="form.banocUrl.value.value"
                            :showErrorMessage="true"
                            :error="form.banocUrl.errorMessage.value"
                            @blur="form.banocUrl.handleChange"
                            width="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="border-t border-gray-200 my-2"></div>

                    <FormSectionTitle :text="$t('projects.form.section.localization')" />
                    <LocationSelector
                      @update:model-value="form.geoData.handleChange"
                      v-model="form.geoData.value.value as GeoData"
                      :error-message="form.geoData.errorMessage.value"
                    />

                    <FormSectionTitle :text="$t('projects.form.section.thematics')" />
                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="thematics"
                        v-model="form.thematics.value.value"
                        :label="$t('projects.form.section.thematics')"
                        :options="Object.values(Thematic)"
                        :getLabel="(item) => $t('forms.thematics.' + item)"
                        :getValue="(item) => item"
                        :error="form.thematics.errorMessage.value"
                        @blur="form.thematics.handleChange(form.thematics.value.value)"
                        multiple
                      />
                    </div>
                    <div class="flex flex-col gap-1" v-if="otherThematicIsSelected">
                      <BaseInput
                        id="otherThematic"
                        :label="$t('projects.form.section.otherThematic')"
                        v-model="form.otherThematic.value.value"
                        :showErrorMessage="true"
                        :error="form.otherThematic.errorMessage.value"
                        @blur="form.otherThematic.handleChange"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="odds"
                        v-model="form.odds.value.value"
                        :label="$t('forms.odds.title') + ' *'"
                        :options="Object.values(ODD)"
                        :getLabel="(item) => $t('forms.odds.' + item)"
                        :getValue="(item) => item"
                        :error="form.odds.errorMessage.value"
                        @blur="form.odds.handleChange(form.odds.value.value)"
                        multiple
                      />
                    </div>

                    <FormSectionTitle :text="$t('projects.form.section.beneficiaryTypes')" />
                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="beneficiaryTypes"
                        v-model="form.beneficiaryTypes.value.value"
                        :label="$t('projects.form.section.beneficiaryTypes')"
                        :options="Object.values(BeneficiaryType)"
                        :getLabel="(item) => $t('beneficiaryType.' + item)"
                        :getValue="(item) => item"
                        :error="form.beneficiaryTypes.errorMessage.value"
                        @blur="
                          form.beneficiaryTypes.handleChange(form.beneficiaryTypes.value.value)
                        "
                        multiple
                      />
                    </div>
                    <div class="flex flex-col gap-1" v-if="otherBeneficiaryIsSelected">
                      <BaseInput
                        id="otherBeneficiary"
                        :label="$t('projects.form.section.otherBeneficiary')"
                        v-model="form.otherBeneficiary.value.value"
                        :showErrorMessage="true"
                        :error="form.otherBeneficiary.errorMessage.value"
                        @blur="form.otherBeneficiary.handleChange"
                        width="w-full"
                      />
                    </div>

                    <FormSectionTitle :text="$t('projects.form.section.financial')" />
                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="financingTypes"
                        v-model="form.financingTypes.value.value"
                        :label="$t('projects.form.section.financial')"
                        :options="Object.values(ProjectFinancingType)"
                        :getLabel="(item) => $t('projects.financing.' + item)"
                        :getValue="(item) => item"
                        :error="form.financingTypes.errorMessage.value"
                        @blur="form.financingTypes.handleChange(form.financingTypes.value.value)"
                        multiple
                      />
                    </div>
                    <div class="flex flex-col gap-1" v-if="otherFinancialTypeIsSelected">
                      <BaseInput
                        id="otherFinancingType"
                        :label="$t('projects.form.section.otherFinancingType')"
                        v-model="form.otherFinancingType.value.value"
                        :showErrorMessage="true"
                        :error="form.otherFinancingType.errorMessage.value"
                        @blur="form.otherFinancingType.handleChange"
                        width="w-full"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="fundingAmount"
                        :label="$t('projects.form.fields.fundingAmount.label')"
                        v-model="form.fundingAmount.value.value"
                        :showErrorMessage="true"
                        :error="form.fundingAmount.errorMessage.value"
                        @blur="form.fundingAmount.handleChange"
                        :placeholder="$t('projects.form.fields.fundingAmount.placeholder')"
                        width="w-full"
                        type="text"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <div>
                        <FormSectionTitle
                          :text="$t('projects.form.section.actorInCharge')"
                          v-bind="props"
                        />
                        <div class="space-between text-xs text-gray-500 mb-2">
                          <span>{{ $t('projects.form.alterTexts.noActorInCharge') }}</span>
                          <CustomCheckbox
                            checkedColor="text-main"
                            v-model="projectHasNoActorInCharge"
                            :id="'no-actor-in-charge-checkbox'"
                          />
                        </div>
                      </div>

                      <BaseSelect
                        id="MOA"
                        v-if="!projectHasNoActorInCharge"
                        v-model="form.actorsInCharge.value.value as Partial<Actor>[]"
                        :options="actors"
                        :getLabel="(item) => $t('projects.financing.' + item)"
                        :getValue="(item) => item"
                        :error="form.financingTypes.errorMessage.value"
                        multiple
                      />

                      <BaseInput
                        v-else
                        id="otherActorInCharge"
                        :placeholder="$t('projects.form.fields.otherActor.label')"
                        v-model="form.otherActorInCharge.value.value"
                        :showErrorMessage="true"
                        :error="
                          projectHasNoActorInCharge && !form.otherActorInCharge.value.value
                            ? $t('projects.form.errorMessages.noActor')
                            : ''
                        "
                        @blur="form.otherActorInCharge.handleChange"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <div>
                        <FormSectionTitle
                          :text="$t('projects.form.section.projectOwner')"
                          v-bind="props"
                        />
                        <div class="space-between text-xs text-gray-500 mb-2">
                          <span>{{ $t('projects.form.alterTexts.noProjectOwner') }}</span>
                          <CustomCheckbox
                            checkedColor="text-main"
                            v-model="projectHasNoOwner"
                            :id="'no-project-owner-checkbox'"
                          />
                        </div>
                      </div>

                      <BaseSelect
                        id="RPO"
                        v-if="!projectHasNoOwner"
                        v-model="form.actor.value.value"
                        :options="actors"
                        :getValue="(item) => item"
                        :showErrorMessage="true"
                        :error="form.financingTypes.errorMessage.value"
                        multiple
                      />

                      <BaseInput
                        v-else
                        id="otherActor"
                        :placeholder="$t('projects.form.fields.otherActor.label')"
                        v-model="form.otherActor.value.value"
                        :showErrorMessage="true"
                        :error="
                          projectHasNoOwner && !form.otherActor.value.value
                            ? $t('projects.form.errorMessages.noActor')
                            : ''
                        "
                        @blur="form.otherActor.handleChange"
                        width="w-full"
                      />
                    </div>

                    <FormSectionTitle :text="$t('projects.form.section.focalPoint')" />

                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="focalPointName"
                        :label="$t('projects.form.fields.focalPointName.label')"
                        v-model="form.focalPointName.value.value"
                        :placeholder="$t('projects.form.fields.focalPointName.label')"
                        :showErrorMessage="true"
                        :error="form.focalPointName.errorMessage.value"
                        @blur="form.focalPointName.handleChange"
                        width="w-full"
                        required
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="focalPointPosition"
                        :label="$t('projects.form.fields.focalPointPosition.label')"
                        v-model="form.focalPointPosition.value.value"
                        :placeholder="$t('projects.form.fields.focalPointPosition.label')"
                        :showErrorMessage="true"
                        :error="form.focalPointPosition.errorMessage.value"
                        @blur="form.focalPointPosition.handleChange"
                        width="w-full"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="focalPointEmail"
                        :label="$t('projects.form.fields.focalPointEmail.label')"
                        v-model="form.focalPointEmail.value.value"
                        :placeholder="$t('projects.form.fields.focalPointEmail.label')"
                        :showErrorMessage="true"
                        :error="form.focalPointEmail.errorMessage.value"
                        @blur="form.focalPointEmail.handleChange"
                        width="w-full"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700">{{
                        $t('projects.form.fields.focalPointTel.label')
                      }}</label>
                      <vue-tel-input
                        v-model="form.focalPointTel.value.value"
                        @validate="phoneValidation"
                        class="border border-gray-300 rounded focus-within:ring-1 focus-within:ring-main"
                      ></vue-tel-input>
                    </div>

                    <div class="border-t border-gray-200 my-2"></div>

                    <FormSectionTitle :text="$t('projects.form.section.projectImages')" />
                    <ImagesLoader
                      @updateFiles="handleImagesUpdate"
                      :existingImages="existingImages"
                    />

                    <FormSectionTitle :text="$t('projects.form.section.partnerLogos')" />
                    <ImagesLoader
                      @updateFiles="handleImagesPartnerUpdate"
                      :existingImages="existingPartnerImages"
                      :externalImagesLoader="false"
                    />
                  </fieldset>
                </form>
              </div>

              <div
                class="px-6 py-4 flex justify-between items-center border-t border-gray-200 bg-gray-50"
              >
                <button
                  type="button"
                  class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline transition-colors"
                  @click="$emit('close')"
                >
                  {{ $t('forms.cancel') }}
                </button>

                <button
                  type="submit"
                  form="project-form"
                  :disabled="isSubmitting"
                  class="inline-flex justify-center rounded-md bg-main hover:bg-main-hover px-6 py-2.5 text-sm font-medium text-white focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors items-center"
                >
                  <Icon v-if="isSubmitting" icon="eos-icons:loading" class="mr-2 h-4 w-4" />
                  {{ submitLabel }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import ImagesLoader from '@/components/forms/ImagesLoader.vue'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
import LocationSelector from '@/components/forms/LocationSelector.vue'
import TextEditor from '@/components/forms/TextEditor.vue'
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue'
import FormSectionTitle from '@/components/text-elements/FormSectionTitle.vue'
import { AdministrativeScope } from '@/models/enums/AdministrativeScope'
import { FormType } from '@/models/enums/app/FormType'
import { BeneficiaryType } from '@/models/enums/contents/BeneficiaryType'
import { ODD } from '@/models/enums/contents/ODD'
import { ProjectFinancingType } from '@/models/enums/contents/ProjectFinancingType'
import { Status } from '@/models/enums/contents/Status'
import { Thematic } from '@/models/enums/contents/Thematic'
import type { ContentImageFromUserFile } from '@/models/interfaces/ContentImage'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import type { BaseMediaObject } from '@/models/interfaces/object/MediaObject'
import { type Project, type ProjectSubmission } from '@/models/interfaces/Project'
import { i18n } from '@/plugins/i18n'
import { nestedObjectsToIri } from '@/services/api/ApiPlatformService'
import NewSubmission from '@/views/admin/components/form/NewSubmission.vue'
import { onInvalidSubmit } from '@/services/forms/FormService'
import { ProjectFormService } from '@/services/projects/ProjectFormService'
import { useActorsStore } from '@/stores/actorsStore'
import { useAdminBoundariesStore } from '@/stores/adminBoundariesStore'
import { useProjectStore } from '@/stores/projectStore'
//import { useUserStore } from '@/stores/userStore'
import { computed, onMounted, type Ref, ref } from 'vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseSelect from '@/components/ui/BaseSelect.vue'
import Autocomplete from '@/components/ui/Autocomplete.vue'
import { POSITION, useToast } from 'vue-toastification'
import type { Actor } from '@/models/interfaces/Actor'

const projectStore = useProjectStore()
const actorsStore = useActorsStore()
const adminBoundariesStore = useAdminBoundariesStore()
const toast = useToast()

//const userStore = useUserStore()

const props = defineProps<{
  type: FormType
  project: Project | null
  isShown: boolean
}>()

const projectHasNoOwner = ref(false)

const existingLogo = ref<(BaseMediaObject | string)[]>([])
const existingImages = ref<(BaseMediaObject | string)[]>([])
const existingPartnerImages = ref<BaseMediaObject[]>([])
let existingHostedImages: BaseMediaObject[] = []
let existingExternalImages: string[] = []
let existingHostedPartnerImages: BaseMediaObject[] = []
const imagesToUpload: Ref<ContentImageFromUserFile[]> = ref([])
const imagesPartnerToUpload: Ref<ContentImageFromUserFile[]> = ref([])
const newLogo: Ref<ContentImageFromUserFile[]> = ref([])

const submitLabel = computed(() => {
  /* if (userStore.userIsAdmin() && props.type === FormType.CREATE) {
    return i18n.t('forms.continue')
  } */
  return i18n.t('forms.' + props.type)
})

const actors = computed(() => actorsStore.actorsList)

const otherThematicIsSelected = computed(() => {
  if (form.thematics.value?.value && Array.isArray(form.thematics.value?.value)) {
    return (form.thematics.value?.value as Thematic[]).includes(Thematic.OTHERS)
  }
  return false
})
const otherBeneficiaryIsSelected = computed(() => {
  if (form.beneficiaryTypes.value?.value && Array.isArray(form.beneficiaryTypes.value?.value)) {
    return (form.beneficiaryTypes.value?.value as BeneficiaryType[]).includes(
      BeneficiaryType.OTHERS,
    )
  }
  return false
})
const otherFinancialTypeIsSelected = computed(() => {
  if (form.financingTypes.value?.value && Array.isArray(form.financingTypes.value?.value)) {
    return (form.financingTypes.value?.value as ProjectFinancingType[]).includes(
      ProjectFinancingType.OTHER,
    )
  }
  return false
})

const projectHasNoActorInCharge = ref(false)

const emit = defineEmits(['submitted', 'close'])

const { form, handleSubmit, isSubmitting } = ProjectFormService.getForm(props.project)
const activeAdminLevels = computed(() => {
  if (
    form.administrativeScopes.value?.value &&
    Array.isArray(form.administrativeScopes.value?.value)
  ) {
    return {
      admin1: (form.administrativeScopes.value?.value as AdministrativeScope[]).includes(
        AdministrativeScope.REGIONAL,
      ),
      admin3: (form.administrativeScopes.value?.value as AdministrativeScope[]).includes(
        AdministrativeScope.CITY,
      ),
    }
  }
  return false
})

onMounted(async () => {
  await Promise.all([
    actorsStore.getAll(),
    adminBoundariesStore.getAdmin1(),
    adminBoundariesStore.getAdmin3(),
  ])
  if (props.project) {
    if (props.project.otherActor) {
      projectHasNoOwner.value = true
    }
    if (props.project.otherActorInCharge) {
      projectHasNoActorInCharge.value = true
    }
    existingLogo.value = props.project.logo ? [props.project.logo] : []
    existingImages.value = [...props.project.images, ...props.project.externalImages]
    existingHostedImages = props.project.images
    existingExternalImages = props.project.externalImages

    existingPartnerImages.value = props.project.partners
    existingHostedPartnerImages = props.project.partners
  }
})

const formError = ref<boolean>(false)
let internationalPhoneNumber: string | null = null

function phoneValidation(phoneObject: any) {
  form.focalPointTel.value.value = phoneObject.nationalNumber
  internationalPhoneNumber = phoneObject.number
}

const submitForm = handleSubmit(
  async (values: Partial<Project | ProjectSubmission>) => {
    formError.value = false
    let projectSubmission: ProjectSubmission = nestedObjectsToIri(values)
    if ([FormType.EDIT, FormType.VALIDATE].includes(props.type) && props.project) {
      projectSubmission.id = props.project.id
    }
    projectSubmission = {
      ...projectSubmission,
      images: existingHostedImages,
      partners: existingHostedPartnerImages,
      externalImages: existingExternalImages,
      imagesToUpload: [...imagesToUpload.value],
      imagesPartnerToUpload: [...imagesPartnerToUpload.value],
      focalPointTel: internationalPhoneNumber as string,
    }

    projectStore
      .submitProject(projectSubmission, props.type)
      .then((submittedProject) => emit('submitted', submittedProject))
      .catch(() => {
        toast.error(i18n.t('notifications.user.createError'), {
          timeout: 3000,
          position: POSITION.TOP_CENTER,
        })
      })
  },
  () => {
    formError.value = true
    onInvalidSubmit()
  },
)

function handleImagesUpdate(lists: any) {
  imagesToUpload.value = lists.selectedFiles
  existingExternalImages = []
  existingHostedImages = []
  lists.existingImages.forEach((image: BaseMediaObject | string) => {
    if (typeof image === 'string') {
      existingExternalImages.push(image)
    } else {
      existingHostedImages.push(image)
    }
  })
}

function handleImagesPartnerUpdate(lists: any) {
  imagesPartnerToUpload.value = lists.selectedFiles
  existingHostedPartnerImages = []
  lists.existingImages.forEach((image: BaseMediaObject) => {
    existingHostedPartnerImages.push(image)
  })
}

function handleLogoUpdate(list: any) {
  newLogo.value = list.selectedFiles
}
</script>
